import { getRouteProviderTypes } from "./routesApi";
import { ProviderType } from "../../components/common/ObjectTypes";
import { filter_page_size } from "../apiUtils";
import { v1_technologies } from "../../components/common/gundiV1Technologies";

/**
 * Loads and merges provider types from the API and the v1 static list.
 * When a `signal` is provided, callers MUST check `signal.aborted` before
 * consuming the returned array — an aborted request returns `[[], null]`,
 * which is indistinguishable from a successful empty result.
 */
export const loadProviderTypes = async (
    keycloak_token: string,
    providerTypesSearchInput: string,
    signal?: AbortSignal,
): Promise<[ProviderType[], Error | null]> => {
    try {
        const query = {
            limit: filter_page_size,
            search: providerTypesSearchInput,
            search_fields: "name,value",
            ordering: "name",
        };

        const temp = await getRouteProviderTypes(keycloak_token, query, signal);
        const apiResults: ProviderType[] = temp?.results ?? [];

        if (apiResults.length === 0 && providerTypesSearchInput.length === 0) {
            return [[], null];
        }

        // Remove duplicates from API results
        const uniqueApiResults = Array.from(new Set(apiResults.map((obj) => obj.id))).map(
            (id) => apiResults.find((obj) => obj.id === id)!
        );

        // Filter v1 technologies by search query
        const filteredV1Results = v1_technologies.filter((provider) => {
            const providerName = provider.name?.toLowerCase() ?? "";
            const providerValue = provider.value?.toLowerCase() ?? "";
            const searchQuery = providerTypesSearchInput.toLowerCase();
            return providerName.includes(searchQuery) || providerValue.includes(searchQuery);
        });

        // If no API results but search matches v1 items, return only those
        if (uniqueApiResults.length === 0 && providerTypesSearchInput.length > 0) {
            return [filteredV1Results.sort((a, b) => a.name.localeCompare(b.name)), null];
        }

        // Merge API results with v1 items and sort alphabetically
        const combinedResults = [...uniqueApiResults, ...filteredV1Results].sort((a, b) =>
            a.name.localeCompare(b.name)
        );

        return [combinedResults, null];
    } catch (err: any) {
        // Silently ignore aborted requests — they are intentionally cancelled by the caller
        if (err?.name === "AbortError" || err?.code === "ERR_CANCELED") return [[], null];
        // Do not log here — session-expiry errors are handled globally by the api401 interceptor
        return [[], err];
    }
};
